import { Alert, AlertTitle, Container } from "@mui/material";
import React from "react";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";
import { generateCorrelationId } from "./logging/correlationId";

import { sanitizeAdminRouteForLogging } from "./logging/sanitizeAdminRouteForLogging";
import {
    DEFAULT_PUBLIC_MESSAGE,
    coercePublicErrorResponse,
    formatPublicErrorForDisplay,
    sanitizePublicMessage,
} from "./errors/publicErrorContract";

const logger = FrontendLoggerFactory.getLogger().withComponent(
    "utils/errorHandling",
);

const DEFAULT_ERROR_BOUNDARY_OPERATION = "UNCAUGHT_REACT_ERROR";
const DEFAULT_ERROR_BOUNDARY_FEATURE = "FND";

const isErrorRecord = (value: unknown): value is Record<string, unknown> => {
    return Boolean(value && typeof value === "object");
};

const getNumberField = (
    record: Record<string, unknown>,
    field: string,
): number | undefined => {
    const value = record[field];
    return typeof value === "number" ? value : undefined;
};

const getStringField = (
    record: Record<string, unknown>,
    field: string,
): string | undefined => {
    const value = record[field];
    return typeof value === "string" ? value : undefined;
};

const getRecordField = (
    record: Record<string, unknown>,
    field: string,
): Record<string, unknown> | undefined => {
    const value = record[field];
    return value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
};

const buildPublicErrorInput = (
    errorRecord: Record<string, unknown>,
    fallbackMessage: string,
): {
    status?: number;
    data: unknown;
    correlationId: string | null;
} => {
    const responseRecord = getRecordField(errorRecord, "response");
    const responseStatus =
        responseRecord ? getNumberField(responseRecord, "status") : undefined;
    const responseData = responseRecord?.["data"];

    return {
        status: responseStatus ?? getNumberField(errorRecord, "status"),
        correlationId: getStringField(errorRecord, "correlationId") ?? null,
        data:
            responseData ??
            errorRecord["data"] ??
            errorRecord["message"] ??
            fallbackMessage,
    };
};

const getRawErrorMessage = (error: unknown): string | undefined => {
    if (error instanceof Error) {
        return error.message;
    }

    if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message?: unknown }).message === "string"
    ) {
        return (error as { message: string }).message;
    }

    return undefined;
};

/**
 * Application-level error wrapper used to add stable codes and (optionally) HTTP status information.
 *
 * @remarks
 * Prefer throwing `AppError` at UI/service boundaries so error displays can remain user-friendly while logs
 * preserve the underlying failure details.
 */
export class AppError extends Error {
    static fromApiError(apiError: {
        status: number;
        data: unknown;
        message: string;
    }): AppError {
        return new AppError(
            apiError.message || "An API error occurred",
            "API_ERROR",
            apiError.status,
        );
    }

    static fromFirebaseError(firebaseError: unknown): AppError {
        // Map Firebase error codes to user-friendly messages
        const errorMessages: Record<string, string> = {
            "auth/user-not-found": "No account found with this email address",
            "auth/wrong-password": "Incorrect password",
            "auth/too-many-requests":
                "Too many failed attempts. Please try again later",
            "auth/network-request-failed":
                "Network error. Please check your connection",
        };

        const code =
            firebaseError &&
            typeof firebaseError === "object" &&
            "code" in firebaseError &&
            typeof (firebaseError as { code?: unknown }).code === "string"
                ? (firebaseError as { code: string }).code
                : undefined;

        const fallbackMessage = sanitizePublicMessage(
            getRawErrorMessage(firebaseError) ?? "Authentication error",
        );

        const message =
            (code ? errorMessages[code] : undefined) ?? fallbackMessage;

        return new AppError(
            message,
            "AUTH_ERROR",
            undefined,
            firebaseError instanceof Error ? firebaseError : undefined,
        );
    }

    constructor(
        message: string,
        readonly code: string,
        readonly statusCode?: number,
        readonly originalError?: Error,
    ) {
        super(message);
        this.name = "AppError";
    }
}

interface ErrorBoundaryProps {
    children?: React.ReactNode;
    componentName?: string;
    feature?: string;
    operation?: string;
    route?: string;
}

// Error boundary component
interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

export class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    private hasLoggedError = false;

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        if (this.hasLoggedError) {
            return;
        }

        this.hasLoggedError = true;

        const boundaryLogger = logger
            .withComponent(this.props.componentName ?? "AppErrorBoundary")
            .withCorrelationId(generateCorrelationId());

        boundaryLogger.error(
            "Unhandled React error boundary caught an uncaught exception",
            error,
            {
                feature: this.props.feature ?? DEFAULT_ERROR_BOUNDARY_FEATURE,
                operation: this.props.operation ?? DEFAULT_ERROR_BOUNDARY_OPERATION,
                componentStack: errorInfo.componentStack,
                route:
                    this.props.route ??
                    (typeof window !== "undefined"
                        ? sanitizeAdminRouteForLogging(window.location.pathname)
                        : undefined),
            },
        );
    }

    override render() {
        if (this.state.hasError) {
            const safeMessage = this.state.error
                ? getErrorMessage(this.state.error, DEFAULT_PUBLIC_MESSAGE)
                : DEFAULT_PUBLIC_MESSAGE;
            return React.createElement(
                Container,
                {},
                React.createElement(
                    Alert,
                    { severity: "error" },
                    React.createElement(AlertTitle, {}, "Something went wrong"),
                    safeMessage,
                ),
            );
        }

        return this.props.children;
    }
}

// Utility functions for common error handling patterns
export const handleAsyncError = async <T>(
    operation: () => Promise<T>,
    errorContext: string,
): Promise<T> => {
    try {
        return await operation();
    } catch (error) {
        const coercedError = toError(error);
        logger.error(`Error in ${errorContext}`, coercedError, {
            feature: "FND",
            operation: "ASYNC_ERROR",
            errorContext,
        });
        throw error instanceof AppError
            ? error
            : new AppError(
                  `Failed to ${errorContext}`,
                  "ASYNC_ERROR",
                  undefined,
                  coercedError,
              );
    }
};

/**
 * Converts unknown values into an `Error` for consistent logging + error handling.
 *
 * @remarks
 * Browser APIs sometimes throw `DOMException` (not always an `Error` instance) and some libraries throw
 * plain objects/strings. We normalise those into a real `Error` so our logger can safely capture
 * `{ name, message, stack }` without unsafe casts.
 */
export const toError = (error: unknown): Error | undefined => {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === "string") {
        return new Error(error);
    }

    if (error && typeof error === "object") {
        const name =
            "name" in error && typeof error.name === "string"
                ? error.name
                : undefined;
        const message = getRawErrorMessage(error);

        if (message) {
            const coerced = new Error(message);
            if (name) {
                coerced.name = name;
            }
            return coerced;
        }
    }

    return undefined;
};

export const isAbortError = (error: unknown): boolean => {
    if (!error || typeof error !== "object") {
        return false;
    }

    return "name" in error && error.name === "AbortError";
};

export const isNetworkError = (error: unknown): boolean => {
    const message = getRawErrorMessage(error);

    return (
        typeof message === "string" &&
        (message.includes("Network Error") ||
            message.includes("fetch") ||
            message.includes("ERR_NETWORK"))
    );
};

export const getErrorMessage = (
    error: unknown,
    fallbackMessage: string = DEFAULT_PUBLIC_MESSAGE,
): string => {
    if (error instanceof AppError) {
        return sanitizePublicMessage(
            getRawErrorMessage(error) ?? DEFAULT_PUBLIC_MESSAGE,
        );
    }

    if (isErrorRecord(error)) {
        const publicError = coercePublicErrorResponse(
            buildPublicErrorInput(error, fallbackMessage),
        );

        const resolvedMessage =
            publicError.message === DEFAULT_PUBLIC_MESSAGE
                ? sanitizePublicMessage(fallbackMessage)
                : publicError.message;

        return formatPublicErrorForDisplay({
            ...publicError,
            message: resolvedMessage,
        });
    }

    if (error instanceof Error) {
        const safeMessage = sanitizePublicMessage(
            getRawErrorMessage(error) ?? DEFAULT_PUBLIC_MESSAGE,
        );
        if (safeMessage !== DEFAULT_PUBLIC_MESSAGE) {
            return safeMessage;
        }
        return sanitizePublicMessage(fallbackMessage);
    }

    if (typeof error === "string") {
        const safeMessage = sanitizePublicMessage(error);
        if (safeMessage !== DEFAULT_PUBLIC_MESSAGE) {
            return safeMessage;
        }
        return sanitizePublicMessage(fallbackMessage);
    }

    return sanitizePublicMessage(fallbackMessage);
};
